import type { MapMarker } from './Map';
import placeActiveSvg from '../../../assets/map/place-active.svg';
import { createSelectableMarkerRoot } from './createSelectableMarkerRoot';
import styles from './Map.module.css';

type PlaceMarkerKind = Extract<
  MapMarker['kind'],
  'destination' | 'location' | 'origin'
>;

export interface PlaceMarkerProps {
  id: string;
  coordinate: [number, number];
  name?: string;
  kind?: PlaceMarkerKind;
}

const PLACE_MARKER_LABELS: Record<PlaceMarkerKind, string> = {
  destination: 'Journey destination',
  location: 'Selected location',
  origin: 'Journey origin',
};

export function createPlaceMarker({
  id,
  coordinate,
  name,
  kind = 'location',
}: PlaceMarkerProps): MapMarker {
  return {
    id,
    coordinate,
    name,
    kind,
  };
}

export function isPlaceMarkerKind(
  kind: MapMarker['kind'],
): kind is PlaceMarkerKind {
  return kind === 'location' || kind === 'origin' || kind === 'destination';
}

function createMarkerRoot(
  marker: MapMarker,
  onSelect?: (markerId: string) => void,
): HTMLButtonElement | HTMLDivElement {
  const markerKind = isPlaceMarkerKind(marker.kind) ? marker.kind : 'location';
  return createSelectableMarkerRoot({
    markerId: marker.id,
    ariaLabel: marker.name ?? PLACE_MARKER_LABELS[markerKind],
    className: styles.placeMarkerButton,
    onSelect,
  });
}

export function createPlaceMarkerElement(
  marker: MapMarker,
  onSelect?: (markerId: string) => void,
): HTMLButtonElement | HTMLDivElement {
  const root = createMarkerRoot(marker, onSelect);
  const markerKind = isPlaceMarkerKind(marker.kind) ? marker.kind : 'location';

  if (markerKind === 'location') {
    const image = document.createElement('img');
    image.className = styles.placeMarkerImage;
    image.src = placeActiveSvg;
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    root.appendChild(image);

    return root;
  }

  const markerBody = document.createElement('span');
  markerBody.className = styles.journeyEndpointMarker;
  markerBody.dataset.kind = markerKind;

  const bubble = document.createElement('span');
  bubble.className = styles.journeyEndpointMarkerBubble;
  bubble.setAttribute('aria-hidden', 'true');

  const pin = document.createElement('span');
  pin.className = styles.journeyEndpointMarkerPin;
  pin.setAttribute('aria-hidden', 'true');

  markerBody.appendChild(bubble);
  markerBody.appendChild(pin);
  root.appendChild(markerBody);

  return root;
}
